import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Grid,
  Group,
  ThemeIcon,
  Stack,
  Button,
  Skeleton,
  Table,
  Badge,
  Tooltip,
  ActionIcon,
  UnstyledButton,
} from "@mantine/core";
import {
  IconFileText,
  IconFolder,
  IconClock,
  IconX,
  IconPackage,
  IconAlertTriangle,
} from "@tabler/icons-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store";
import { useTaskStore } from "../store/task.store";
import { useDashboardStats, useCases } from "../hooks/useCases";
import { useNeedingAttention } from "../hooks/useSendOuts";
import { useErrorReports } from "../hooks/useWorkflows";
import { getDisplayCaseNumber } from "../utils/caseIdentity";
import { buildDashboardChartData } from "./dashboardChartData";
import {
  formatDateOnlyWithShortMonth,
  getLabTodayDateOnly,
  parseDateOnly,
} from "../utils/dateOnly";

export function formatExpectedReturnDate(value: unknown): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyWithShortMonth(dateOnly) : "—";
}

// --- Types ---

type PanelKey = "casesToday" | "blocked" | "errors";

interface CaseRow {
  id: string;
  caseNumber: string;
  patient?: string;
  facility?: string;
  workflow?: string;
  status?: string;
  isBlocked?: boolean;
  isRush?: boolean;
  rushReason?: string;
  isHighPriority?: boolean;
  highPriorityReason?: string;
}

// --- Colors ---

const BAR_COLOR = "var(--mantine-color-gray-3)";
const BLOCKED_COLOR = "var(--mantine-color-red-6)";
const ERROR_COLOR = "var(--mantine-color-orange-6)";

const STAGE_ROUTES: Record<string, string> = {
  Grossing: "/grossing",
  Embedding: "/embedding",
  Cutting: "/cutting",
  Staining: "/staining",
  Distribution: "/distribution",
  Review: "/pathologist-review",
};

// --- Sub-components ---

function CaseTable({
  rows,
  onCaseClick,
}: {
  rows: CaseRow[];
  onCaseClick: (id: string) => void;
}) {
  return (
    <Table highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Case Number</Table.Th>
          <Table.Th>Patient</Table.Th>
          <Table.Th>Facility</Table.Th>
          <Table.Th>Workflow</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Priority</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr
            key={row.id}
            style={{ cursor: "pointer" }}
            onClick={() => onCaseClick(row.id)}
          >
            <Table.Td fw={500}>{row.caseNumber}</Table.Td>
            <Table.Td>{row.patient || "-"}</Table.Td>
            <Table.Td>{row.facility || "-"}</Table.Td>
            <Table.Td>{row.workflow || "-"}</Table.Td>
            <Table.Td>
              <Badge
                color={row.isBlocked ? "red" : "blue"}
                variant="light"
                size="sm"
              >
                {row.isBlocked ? "BLOCKED" : row.status || "Unknown"}
              </Badge>
            </Table.Td>
            <Table.Td>
              {row.isRush ? (
                <Tooltip
                  label={row.rushReason || "Rush case - expedited processing"}
                  multiline
                  maw={300}
                >
                  <Badge color="red" variant="filled" size="sm">
                    RUSH
                  </Badge>
                </Tooltip>
              ) : row.isHighPriority ? (
                <Tooltip
                  label={
                    row.highPriorityReason ||
                    "High priority - flagged for attention"
                  }
                  multiline
                  maw={300}
                >
                  <Badge color="yellow" variant="filled" size="sm">
                    PRIORITY
                  </Badge>
                </Tooltip>
              ) : (
                <Badge color="gray" variant="light" size="sm">
                  NORMAL
                </Badge>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

// --- Main component ---

export default function Dashboard() {
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const { selectedTasks } = useTaskStore();
  const [activePanel, setActivePanel] = useState<PanelKey | null>(null);

  const hasTask = (slug: string) => selectedTasks.some((t) => t.slug === slug);

  const toggle = useCallback(
    (key: PanelKey) => setActivePanel((prev) => (prev === key ? null : key)),
    []
  );

  // --- Data fetching ---
  const { data: dashboardStats, isLoading: isLoadingStats } =
    useDashboardStats();
  const { data: pendingErrors, isLoading: isLoadingErrors } = useErrorReports(
    { status: "pending", limit: 20 },
    {
      enabled: activePanel === "errors",
      refetchInterval: false,
      refetchOnWindowFocus: false,
    }
  );

  const todayStr = useMemo(() => getLabTodayDateOnly(), []);
  const { data: todayCasesData, isLoading: isLoadingTodayCases } = useCases(
    { dateFrom: todayStr },
    1,
    20
  );
  const { data: blockedCasesData, isLoading: isLoadingBlockedCases } = useCases(
    { isBlocked: true },
    1,
    20
  );
  const { data: recentCasesData, isLoading: isLoadingRecentCases } = useCases(
    {},
    1,
    20
  );

  const recentCases = useMemo(
    () =>
      (recentCasesData?.data ?? [])
        .filter((c) => c.currentStatus?.name?.toLowerCase() !== "draft")
        .slice(0, 10)
        .map((c) => ({
          id: c.id,
          caseNumber: getDisplayCaseNumber(c),
          patient: c.patient
            ? `${c.patient.lastName}, ${c.patient.firstName}`
            : undefined,
          facility: c.facility?.name,
          workflow: c.workflow?.accessioningType
            ? c.workflow.accessioningType
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l: string) => l.toUpperCase())
            : undefined,
          status: c.currentStatus?.name,
          isBlocked: c.isBlocked,
          isRush: c.isRush,
          rushReason: c.rushReason ?? undefined,
          isHighPriority: c.isHighPriority,
          highPriorityReason: c.highPriorityReason ?? undefined,
        })),
    [recentCasesData]
  );

  // --- Chart data ---
  const chartData = useMemo(
    () => buildDashboardChartData(dashboardStats?.stageCounts ?? []),
    [dashboardStats?.stageCounts]
  );

  const { data: needingAttention, isLoading: isLoadingAttention } =
    useNeedingAttention();

  const totalBlocked = dashboardStats?.blocked ?? 0;
  const totalErrors = dashboardStats?.pendingErrorCount ?? 0;
  const totalNeedingAttention =
    (needingAttention?.rejectedCount ?? 0) +
    (needingAttention?.overdueCount ?? 0);

  // --- Helper to map full Case objects to CaseRow ---
  const casesToRows = useCallback(
    (cases: any[], overrides?: Partial<CaseRow>): CaseRow[] =>
      cases.map((c) => ({
        id: c.id,
        caseNumber: getDisplayCaseNumber(c),
        patient: c.patient
          ? `${c.patient.lastName}, ${c.patient.firstName}`
          : undefined,
        facility: c.facility?.name,
        workflow: c.workflow?.accessioningType
          ? c.workflow.accessioningType
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase())
          : undefined,
        status: c.currentStatus?.name,
        isBlocked: c.isBlocked,
        isRush: c.isRush,
        rushReason: c.rushReason,
        isHighPriority: c.isHighPriority,
        highPriorityReason: c.highPriorityReason,
        ...overrides,
      })),
    []
  );

  // --- Panel data resolver ---
  const getPanel = (): {
    rows: CaseRow[];
    isLoading: boolean;
    title: string;
  } | null => {
    switch (activePanel) {
      case "casesToday":
        return {
          title: "Cases Received Today",
          isLoading: isLoadingTodayCases,
          rows: casesToRows(todayCasesData?.data ?? []),
        };
      case "blocked":
        return {
          title: "Blocked Cases",
          isLoading: isLoadingBlockedCases,
          rows: casesToRows(blockedCasesData?.data ?? [], { isBlocked: true }),
        };
      case "errors":
        return {
          title: "Pending Error Reports",
          isLoading: isLoadingErrors,
          rows: (pendingErrors ?? []).map((e) => ({
            id: e.caseId,
            caseNumber: getDisplayCaseNumber(e.case) || "-",
            status: `Error: ${
              e.discrepancy?.error ?? e.error?.error ?? "Unknown"
            }`,
            isBlocked: e.blockCase,
          })),
        };
      default:
        return null;
    }
  };

  const panelData = getPanel();

  // --- Chart click handler ---
  const handleBarSectionClick = useCallback(
    (data: any) => {
      const stage = data?.stage as string | undefined;
      const route = stage ? STAGE_ROUTES[stage] : undefined;
      if (route) {
        navigate(route);
      }
    },
    [navigate]
  );

  const goToCase = useCallback(
    (id: string) => navigate(`/cases/${id}`),
    [navigate]
  );

  return (
    <Container size="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Dashboard</Title>
            <Text c="dimmed">Welcome back, {profile?.fullName || "User"}</Text>
          </div>
          <Group>
            {hasTask("front-office") && (
              <Button
                leftSection={<IconFileText size="1rem" />}
                onClick={() => navigate("/accessioning")}
              >
                New Case
              </Button>
            )}
            <Button variant="light" onClick={() => navigate("/cases")}>
              View All Cases
            </Button>
          </Group>
        </Group>

        {/* Two-column: KPI cards (left) + Chart (right) */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md" h="100%">
              {/* Cases Today */}
              <Paper
                withBorder
                p="md"
                radius="md"
                style={{
                  cursor:
                    (dashboardStats?.casesToday ?? 0) > 0
                      ? "pointer"
                      : "default",
                  outline:
                    activePanel === "casesToday"
                      ? "2px solid var(--mantine-color-blue-5)"
                      : "none",
                }}
                onClick={
                  (dashboardStats?.casesToday ?? 0) > 0
                    ? () => toggle("casesToday")
                    : undefined
                }
              >
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed" fw={500}>
                    Cases Today
                  </Text>
                  <ThemeIcon variant="light" color="blue" size="lg">
                    <IconFolder size={24} />
                  </ThemeIcon>
                </Group>
                {isLoadingStats ? (
                  <Skeleton height={28} width={40} mb={4} />
                ) : (
                  <Text size="xl" fw={700}>
                    {dashboardStats?.casesToday ?? 0}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  New cases received today
                </Text>
              </Paper>

              {/* Turnaround Time */}
              <Paper withBorder p="md" radius="md">
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed" fw={500}>
                    Turnaround Time
                  </Text>
                  <ThemeIcon
                    variant="light"
                    color={dashboardStats?.agedCaseCount ? "yellow" : "gray"}
                    size="lg"
                  >
                    <IconClock size={24} />
                  </ThemeIcon>
                </Group>
                {isLoadingStats ? (
                  <Skeleton height={28} width={40} mb={4} />
                ) : (
                  <>
                    <Text size="xl" fw={700}>
                      {dashboardStats?.averageTatDays != null
                        ? `${dashboardStats.averageTatDays}d avg`
                        : "-"}
                    </Text>
                    {(dashboardStats?.agedCaseCount ?? 0) > 0 && (
                      <Text size="sm" fw={600} c="yellow.7" mt={4}>
                        {dashboardStats?.agedCaseCount} cases over 5 days
                      </Text>
                    )}
                  </>
                )}
                <Text size="xs" c="dimmed" mt={4}>
                  Avg age of in-progress cases
                </Text>
              </Paper>
            </Stack>
          </Grid.Col>

          {/* Chart column */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            {isLoadingStats ? (
              <Skeleton height={280} />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} style={{ cursor: "pointer" }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        return (
                          <Paper p="xs" withBorder shadow="sm" radius="sm">
                            <Text fw={600} size="sm" mb={4}>
                              {label}
                            </Text>
                            <Text size="xs">Total: {d.total}</Text>
                            {d.blocked > 0 && (
                              <Text size="xs" c="red">
                                Blocked: {d.blocked}
                              </Text>
                            )}
                            {d.errorsOnly > 0 && (
                              <Text size="xs" c="orange">
                                Errors: {d.errorsOnly}
                              </Text>
                            )}
                            <Text size="xs" c="dimmed" mt={4}>
                              Click to view cases
                            </Text>
                          </Paper>
                        );
                      }}
                    />
                    <Bar
                      dataKey="clean"
                      stackId="a"
                      fill={BAR_COLOR}
                      radius={[0, 0, 0, 0]}
                      legendType="none"
                      onClick={handleBarSectionClick}
                      activeBar={false}
                    />
                    <Bar
                      dataKey="errorsOnly"
                      stackId="a"
                      fill={ERROR_COLOR}
                      legendType="none"
                      onClick={handleBarSectionClick}
                      activeBar={false}
                    />
                    <Bar
                      dataKey="blocked"
                      stackId="a"
                      fill={BLOCKED_COLOR}
                      radius={[4, 4, 0, 0]}
                      legendType="none"
                      onClick={handleBarSectionClick}
                      activeBar={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <Group justify="center" gap="md" mt="xs">
                  {totalBlocked > 0 ? (
                    <UnstyledButton onClick={() => toggle("blocked")}>
                      <Group gap={6}>
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            backgroundColor: BLOCKED_COLOR,
                          }}
                        />
                        <Text
                          size="xs"
                          fw={500}
                          td={
                            activePanel === "blocked" ? "underline" : undefined
                          }
                        >
                          Blocked ({totalBlocked})
                        </Text>
                      </Group>
                    </UnstyledButton>
                  ) : (
                    <Group gap={6}>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: BLOCKED_COLOR,
                          opacity: 0.4,
                        }}
                      />
                      <Text size="xs" fw={500} c="dimmed">
                        Blocked (0)
                      </Text>
                    </Group>
                  )}
                  {totalErrors > 0 ? (
                    <UnstyledButton onClick={() => toggle("errors")}>
                      <Group gap={6}>
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            backgroundColor: ERROR_COLOR,
                          }}
                        />
                        <Text
                          size="xs"
                          fw={500}
                          td={
                            activePanel === "errors" ? "underline" : undefined
                          }
                        >
                          Errors ({totalErrors})
                        </Text>
                      </Group>
                    </UnstyledButton>
                  ) : (
                    <Group gap={6}>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: ERROR_COLOR,
                          opacity: 0.4,
                        }}
                      />
                      <Text size="xs" fw={500} c="dimmed">
                        Errors (0)
                      </Text>
                    </Group>
                  )}
                </Group>
              </>
            )}
          </Grid.Col>
        </Grid>

        {/* Sendouts Needing Attention */}
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <ThemeIcon
                variant="light"
                color={totalNeedingAttention > 0 ? "red" : "gray"}
                size="lg"
              >
                <IconPackage size={20} />
              </ThemeIcon>
              <Title order={4}>Sendouts Needing Attention</Title>
              {totalNeedingAttention > 0 && (
                <Badge color="red" variant="filled" size="sm">
                  {totalNeedingAttention}
                </Badge>
              )}
            </Group>
          </Group>

          {isLoadingAttention ? (
            <Stack gap="sm">
              <Skeleton height={20} />
              <Skeleton height={20} />
            </Stack>
          ) : totalNeedingAttention === 0 ? (
            <Text c="dimmed" size="sm">
              No send-outs require attention
            </Text>
          ) : (
            <Stack gap="md">
              {/* Rejected section */}
              {(needingAttention?.rejected?.length ?? 0) > 0 && (
                <div>
                  <Group gap="xs" mb="xs">
                    <IconAlertTriangle
                      size={14}
                      color="var(--mantine-color-red-6)"
                    />
                    <Text size="sm" fw={600} c="red">
                      Rejected ({needingAttention!.rejectedCount})
                    </Text>
                  </Group>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Case #</Table.Th>
                        <Table.Th>Patient</Table.Th>
                        <Table.Th>Rejection Reason</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {needingAttention!.rejected.map((item) => (
                        <Table.Tr
                          key={item.id}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate(
                              `/send-outs?tab=rejected&highlight=${item.id}`
                            )
                          }
                        >
                          <Table.Td fw={500}>
                            {item.caseNumber || "N/A"}
                          </Table.Td>
                          <Table.Td>{item.patientName || "—"}</Table.Td>
                          <Table.Td>
                            <Text size="sm" c="red" lineClamp={2}>
                              {item.rejectionReason || "No reason provided"}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              )}

              {/* Overdue section */}
              {(needingAttention?.overdue?.length ?? 0) > 0 && (
                <div>
                  <Group gap="xs" mb="xs">
                    <IconClock
                      size={14}
                      color="var(--mantine-color-orange-6)"
                    />
                    <Text size="sm" fw={600} c="orange">
                      Overdue ({needingAttention!.overdueCount})
                    </Text>
                  </Group>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Case #</Table.Th>
                        <Table.Th>Patient</Table.Th>
                        <Table.Th>Expected Return</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {needingAttention!.overdue.map((item) => (
                        <Table.Tr
                          key={item.id}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate(
                              `/send-outs?tab=overdue&highlight=${item.id}`
                            )
                          }
                        >
                          <Table.Td fw={500}>
                            {item.caseNumber || "N/A"}
                          </Table.Td>
                          <Table.Td>{item.patientName || "—"}</Table.Td>
                          <Table.Td>
                            <Text size="sm" c="orange">
                              {formatExpectedReturnDate(
                                item.expectedReturnDate
                              )}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              )}
            </Stack>
          )}
        </Paper>

        {/* Case list — replaces with filtered view when a panel is active */}
        <Paper withBorder p="md" radius="md">
          {panelData ? (
            <>
              <Group justify="space-between" mb="md">
                <Title order={4}>{panelData.title}</Title>
                <ActionIcon
                  variant="subtle"
                  onClick={() => setActivePanel(null)}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
              {panelData.isLoading ? (
                <Stack gap="sm">
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                </Stack>
              ) : panelData.rows.length > 0 ? (
                <>
                  <CaseTable
                    rows={panelData.rows.slice(0, 10)}
                    onCaseClick={goToCase}
                  />
                  {panelData.rows.length > 10 && (
                    <Text size="xs" c="dimmed" mt="xs">
                      Showing 10 of {panelData.rows.length} cases
                    </Text>
                  )}
                </>
              ) : (
                <Text c="dimmed" size="sm">
                  No cases found.
                </Text>
              )}
            </>
          ) : (dashboardStats?.casesToday ?? 0) > 0 ? (
            <>
              <Title order={4} mb="md">
                Cases Received Today
              </Title>
              {isLoadingTodayCases ? (
                <Stack gap="sm">
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                </Stack>
              ) : (todayCasesData?.data ?? []).length > 0 ? (
                <CaseTable
                  rows={casesToRows(todayCasesData?.data ?? [])}
                  onCaseClick={goToCase}
                />
              ) : (
                <Text c="dimmed" size="sm">
                  No cases found.
                </Text>
              )}
            </>
          ) : (
            <>
              <Title order={4} mb="md">
                Recent Cases
              </Title>
              {isLoadingRecentCases ? (
                <Stack gap="sm">
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                </Stack>
              ) : recentCases.length > 0 ? (
                <CaseTable rows={recentCases} onCaseClick={goToCase} />
              ) : (
                <Text c="dimmed" size="sm">
                  No recent cases.
                </Text>
              )}
            </>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}
